import React, { useMemo } from 'react'
import styled from 'styled-components'
import ReactECharts from 'echarts-for-react'
import type { TFunction } from 'i18next'

interface HeightComparisonChartProps {
  currentHeight: number
  maxLegalHeight: number
  neighborsAvgHeight?: number
  neighborsMaxHeight?: number
  t: TFunction
}

const ChartContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  width: 100%;
`

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
  text-align: center;
`

const ChartSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 20px 0;
  text-align: center;
`

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`

const LegendColor = styled.div<{ $color: string }>`
  width: 20px;
  height: 12px;
  background: ${({ $color }) => $color};
  border-radius: 2px;
`

const LegendLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const HeightComparisonChart: React.FC<HeightComparisonChartProps> = ({
  currentHeight,
  maxLegalHeight,
  neighborsAvgHeight,
  neighborsMaxHeight,
  t,
}) => {
  const availableHeight = Math.max(0, maxLegalHeight - currentHeight)
  const utilizationPercent =
    maxLegalHeight > 0
      ? ((currentHeight / maxLegalHeight) * 100).toFixed(1)
      : '0'

  const option = useMemo(() => {
    // Prepare data
    const categories: string[] = []
    const values: number[] = []
    const colors: string[] = []

    // Current building height
    categories.push(t('elevationCharts.height.currentBuilding'))
    values.push(currentHeight)
    colors.push('#3b82f6') // Blue

    // Maximum legal height
    categories.push(t('elevationCharts.height.legalMaximum'))
    values.push(maxLegalHeight)
    colors.push('#ef4444') // Red

    // Neighbors average (if available)
    if (neighborsAvgHeight !== undefined && neighborsAvgHeight > 0) {
      categories.push(t('elevationCharts.height.neighborsAverage'))
      values.push(neighborsAvgHeight)
      colors.push('#8b5cf6') // Purple
    }

    // Neighbors max (if available)
    if (neighborsMaxHeight !== undefined && neighborsMaxHeight > 0) {
      categories.push(t('elevationCharts.height.neighborsMaximum'))
      values.push(neighborsMaxHeight)
      colors.push('#f59e0b') // Orange
    }

    // Calculate max value for axis (add 10% padding)
    const maxValue = Math.max(...values) * 1.1

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          const item = params[0]
          const value = item.value
          const name = item.name.replace('\n', ' ')

          let extra = ''
          if (name === t('elevationCharts.height.currentBuilding').replace('\n', ' ')) {
            extra = `<br/>${t('elevationCharts.height.utilization')}: ${utilizationPercent}%<br/>${t('elevationCharts.height.available')}: ${availableHeight.toFixed(1)}m`
          } else if (name === t('elevationCharts.height.legalMaximum').replace('\n', ' ')) {
            extra = `<br/>${t('elevationCharts.height.maxAllowedByZoning')}`
          }

          return `<strong>${name}</strong><br/>${t('elevationCharts.height.height')}: ${value.toFixed(1)}m${extra}`
        },
      },
      grid: {
        left: '15%',
        right: '10%',
        bottom: '10%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        max: maxValue,
        axisLabel: {
          formatter: '{value}m',
          fontSize: 11,
          color: '#64748b',
        },
        splitLine: {
          lineStyle: {
            color: '#e2e8f0',
            type: 'dashed',
          },
        },
      },
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          fontSize: 12,
          color: '#475569',
          lineHeight: 16,
        },
        axisLine: {
          lineStyle: {
            color: '#cbd5e1',
          },
        },
      },
      series: [
        {
          type: 'bar',
          data: values.map((value, index) => ({
            value,
            itemStyle: {
              color: colors[index],
            },
          })),
          barWidth: '60%',
          label: {
            show: true,
            position: 'right',
            formatter: '{c}m',
            fontSize: 12,
            color: '#475569',
            fontWeight: 'bold',
          },
          markLine:
            availableHeight > 0
              ? {
                  data: [
                    {
                      yAxis: 0, // Current building line
                      lineStyle: {
                        color: '#10b981',
                        width: 2,
                        type: 'dashed',
                      },
                      label: {
                        formatter: t('elevationCharts.height.availableMeters', { meters: availableHeight.toFixed(1) }),
                        position: 'end',
                        color: '#10b981',
                        fontSize: 11,
                      },
                    },
                  ],
                }
              : undefined,
        },
      ],
      animation: true,
      animationDuration: 800,
      animationEasing: 'cubicOut',
    }
  }, [
    currentHeight,
    maxLegalHeight,
    neighborsAvgHeight,
    neighborsMaxHeight,
    availableHeight,
    utilizationPercent,
    t,
  ])

  return (
    <ChartContainer>
      <ChartTitle>{t('elevationCharts.height.title')}</ChartTitle>
      <ChartSubtitle>{t('elevationCharts.height.subtitle')}</ChartSubtitle>

      <ReactECharts
        option={option}
        style={{ height: '300px', width: '100%' }}
        opts={{ renderer: 'svg' }}
      />

      <LegendContainer>
        <LegendItem>
          <LegendColor $color="#3b82f6" />
          <LegendLabel>
            {t('elevationCharts.height.legend.currentBuilding', { height: currentHeight.toFixed(1) })}
          </LegendLabel>
        </LegendItem>

        <LegendItem>
          <LegendColor $color="#ef4444" />
          <LegendLabel>
            {t('elevationCharts.height.legend.legalMaximum', { height: maxLegalHeight.toFixed(1) })}
          </LegendLabel>
        </LegendItem>

        {neighborsAvgHeight !== undefined && neighborsAvgHeight > 0 && (
          <LegendItem>
            <LegendColor $color="#8b5cf6" />
            <LegendLabel>
              {t('elevationCharts.height.legend.neighborsAverage', { height: neighborsAvgHeight.toFixed(1) })}
            </LegendLabel>
          </LegendItem>
        )}

        {neighborsMaxHeight !== undefined && neighborsMaxHeight > 0 && (
          <LegendItem>
            <LegendColor $color="#f59e0b" />
            <LegendLabel>
              {t('elevationCharts.height.legend.neighborsMaximum', { height: neighborsMaxHeight.toFixed(1) })}
            </LegendLabel>
          </LegendItem>
        )}

        {availableHeight > 0 && (
          <LegendItem>
            <LegendColor $color="#10b981" />
            <LegendLabel>
              {t('elevationCharts.height.legend.availableSpace', { height: availableHeight.toFixed(1) })}
            </LegendLabel>
          </LegendItem>
        )}
      </LegendContainer>
    </ChartContainer>
  )
}
